import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/lib/auth';
import { useOrg } from '../OrgProvider';
import { canManageOrg } from '../org-roles';
import {
  adminIngest,
  fetchOrgPackages,
  fetchOrgRepoRequests,
  setOrgPackageAlias,
  setOrgPackageArchived,
} from '../api';
import { navigateTo } from '../router';
import type { PackageSummary, OrgIndexRequest } from '../types';
import { displayPackageName, formatIndexedAt } from '../utils';
import { RegistryBadge } from '../components/RegistryBadge';
import { PanelHeader } from '../components/PanelHeader';
import { OrgSwitcher } from '../components/OrgSwitcher';
import { IndexRepoDialog, OrgRepoRequestsPanel } from '../components/RequestPanels';
import { WorkspacesPanel } from '../components/WorkspacesPanel';

function StatusDot({ status }: { status: string }) {
  const color =
    status === 'done'
      ? 'bg-good'
      : status === 'indexing'
        ? 'bg-accent animate-pulse'
        : status === 'pending'
          ? 'bg-warn'
          : 'bg-del';
  return <span className={`inline-block h-2 w-2 rounded-full ${color}`} />;
}

function DashboardNotice({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-md py-20 text-center">
      <div className="mx-auto inline-flex h-11 w-11 items-center justify-center bg-accent-soft text-accent">
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={1.6}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0H5m14 0h2M5 21H3M9 7h6M9 11h6M9 15h3"
          />
        </svg>
      </div>
      <h2 className="mt-4 text-lg font-semibold text-ink">{title}</h2>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-dim">{body}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

interface PackageRow {
  id: string | null;
  registry: string;
  name: string;
  version: string;
  indexedAt: string;
  status: 'live' | 'indexing';
  sourceUrl: string | null;
  alias: string | null;
}

export function DashboardPage({ isAdmin = false }: { isAdmin?: boolean } = {}) {
  const { user, getToken, deploymentMode } = useAuth();
  const { activeOrg, orgs } = useOrg();
  const canManage = canManageOrg(activeOrg);
  const [packages, setPackages] = useState<PackageSummary[]>([]);
  const [requests, setRequests] = useState<OrgIndexRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [isIndexDialogOpen, setIsIndexDialogOpen] = useState(false);
  const [reindexUrl, setReindexUrl] = useState<string | null>(null);
  const [aliasTarget, setAliasTarget] = useState<PackageRow | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<PackageRow | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [ingestingKey, setIngestingKey] = useState<string | null>(null);
  const [ingestFeedback, setIngestFeedback] = useState<{
    key: string;
    message: string;
    tone: 'success' | 'error';
  } | null>(null);
  const [page, setPage] = useState(0);

  // The org packages endpoint returns the full list, so paging is client-side.
  const PAGE_SIZE = 50;

  useEffect(() => {
    setPage(0);
  }, [activeOrg]);

  useEffect(() => {
    const load = async () => {
      const token = await getToken();
      if (!token || !activeOrg) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      try {
        const [orgPkgs, reqs] = await Promise.all([
          fetchOrgPackages(activeOrg.id, token),
          fetchOrgRepoRequests(activeOrg.id, token),
        ]);
        setPackages(orgPkgs.filter((p: PackageSummary) => !p.hidden));
        setRequests(reqs);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Unable to load dashboard data.');
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [activeOrg, getToken, reloadKey]);

  // Build package rows with status
  const activeRequests = requests.filter((r) => r.status === 'pending' || r.status === 'indexing');
  const indexingNames = new Set(activeRequests.map((r) => r.name));

  const rows: PackageRow[] = packages
    .sort((a, b) => new Date(b.indexed_at).getTime() - new Date(a.indexed_at).getTime())
    .map((pkg) => ({
      id: pkg.id ?? null,
      registry: pkg.registry,
      name: pkg.name,
      version: pkg.version,
      indexedAt: pkg.indexed_at,
      status: indexingNames.has(pkg.name) ? ('indexing' as const) : ('live' as const),
      sourceUrl: pkg.source_url ?? null,
      alias: pkg.short_alias ?? null,
    }));

  const handleAliasSaved = (updated: { packageId: string; alias: string | null }) => {
    setPackages((prev) => {
      const target = prev.find((p) => p.id === updated.packageId);
      if (!target) return prev;
      return prev.map((pkg) =>
        pkg.registry === target.registry && pkg.name === target.name
          ? { ...pkg, short_alias: updated.alias }
          : pkg
      );
    });
  };

  const rowKey = (row: PackageRow) => `${row.registry}:${row.name}:${row.version}`;

  const handleReingestVectors = async (row: PackageRow) => {
    if (!activeOrg) return;
    const key = rowKey(row);
    setIngestingKey(key);
    setIngestFeedback(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('You must be signed in.');
      const result = await adminIngest(
        { registry: row.registry, name: row.name, version: row.version, orgId: activeOrg.id },
        token
      );
      setIngestFeedback({
        key,
        tone: 'success',
        message: `Re-ingested ${result.ingested} vectors.`,
      });
    } catch (err) {
      setIngestFeedback({
        key,
        tone: 'error',
        message: err instanceof Error ? err.message : 'Failed to re-ingest vectors.',
      });
    } finally {
      setIngestingKey(null);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget || !archiveTarget.id || !activeOrg) return;
    setIsArchiving(true);
    setArchiveError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('You must be signed in.');
      await setOrgPackageArchived(activeOrg.id, archiveTarget.id, true, token);
      setPackages((prev) =>
        prev.filter(
          (pkg) => !(pkg.registry === archiveTarget.registry && pkg.name === archiveTarget.name)
        )
      );
      setArchiveTarget(null);
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Failed to archive package.');
    } finally {
      setIsArchiving(false);
    }
  };

  if (!user) {
    return (
      <DashboardNotice
        title="Sign in to view your dashboard"
        body="The dashboard shows your team's privately indexed repos and workspaces."
      />
    );
  }

  if (!activeOrg) {
    return (
      <DashboardNotice
        title="Select an organization"
        body="Create or select an organization to manage your privately indexed repos."
        action={
          <Button size="sm" onClick={() => navigateTo('/org/new')}>
            Create organization
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-7 py-6 sm:space-y-8">
      {/* Page header — org context + primary actions */}
      <header className="flex flex-col gap-4 border-b border-rule pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="truncate text-xl font-semibold tracking-[-0.02em] text-ink sm:text-2xl">
              {activeOrg.name}
            </h1>
            {activeOrg.slug && <span className="chip font-mono text-2xs">{activeOrg.slug}</span>}
            {orgs.length > 1 && <OrgSwitcher />}
          </div>
          <p className="mt-1.5 text-sm text-dim">
            Private repos, workspaces, and indexing activity for this organization.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" onClick={() => setIsIndexDialogOpen(true)}>
            Index repo
          </Button>
          <Button size="sm" variant="secondary" onClick={() => navigateTo('/org/settings')}>
            Settings
          </Button>
        </div>
      </header>

      <IndexRepoDialog open={isIndexDialogOpen} onClose={() => setIsIndexDialogOpen(false)} />
      <IndexRepoDialog
        open={reindexUrl !== null}
        onClose={() => setReindexUrl(null)}
        initialUrl={reindexUrl ?? ''}
      />
      {aliasTarget && activeOrg ? (
        <AliasDialog
          orgId={activeOrg.id}
          row={aliasTarget}
          onClose={() => setAliasTarget(null)}
          onSaved={(next) => {
            if (!aliasTarget.id) return;
            handleAliasSaved({ packageId: aliasTarget.id, alias: next });
            setAliasTarget(null);
          }}
        />
      ) : null}
      {archiveTarget ? (
        <ConfirmDialog
          title={`Archive ${archiveTarget.name}?`}
          message={
            archiveError
              ? `${archiveError}`
              : `Archiving hides every indexed version from the dashboard and query results. You can re-index the repo anytime to restore it.`
          }
          confirmLabel="Archive"
          tone="danger"
          isConfirming={isArchiving}
          onConfirm={() => void confirmArchive()}
          onClose={() => {
            setArchiveTarget(null);
            setArchiveError(null);
          }}
        />
      ) : null}

      <section className="space-y-4">
        <PanelHeader
          title="Private repos"
          count={rows.length}
          description="Indexed repositories available to your organization for retrieval and querying."
          icon={
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.6}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 7.5 12 3l9 4.5M3 7.5 12 12m-9-4.5v9L12 21m0-9 9-4.5M12 12v9m9-13.5v9L12 21"
              />
            </svg>
          }
          action={
            rows.length > PAGE_SIZE ? (
              <span className="text-xs text-dim">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, rows.length)} of{' '}
                {rows.length}
              </span>
            ) : undefined
          }
        />

        <div className="lc-surface overflow-hidden">
          {isLoading ? (
            <div className="px-5 py-8 text-center text-sm text-dim">Loading…</div>
          ) : loadError ? (
            <div className="px-6 py-12 text-center" role="alert">
              <p className="text-sm font-medium text-ink">Couldn't load your repositories.</p>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-dim">{loadError}</p>
              <Button type="button" onClick={() => setReloadKey((k) => k + 1)} className="mt-5">
                Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <div className="mx-auto inline-flex h-11 w-11 items-center justify-center bg-accent-soft text-accent">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={1.6}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 7.5 12 3l9 4.5M3 7.5 12 12m-9-4.5v9L12 21m0-9 9-4.5M12 12v9m9-13.5v9L12 21"
                  />
                </svg>
              </div>
              <h4 className="mt-4 text-base font-semibold text-ink">Index your first repo</h4>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-dim">
                Index an internal repo once and every agent understands its structure — instead of
                grepping around blind. Learn it once here, reuse it across every agent and teammate.
              </p>
              <Button onClick={() => setIsIndexDialogOpen(true)} className="mt-5">
                Index a repo
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-rule-soft bg-bg">
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-dim">
                      Repository
                    </th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-dim">
                      Version
                    </th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-dim">
                      Alias
                    </th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-dim">
                      Updated
                    </th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-rule-soft">
                  {rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((row) => {
                    const treePath = `/${encodeURIComponent(row.registry)}/${encodeURIComponent(row.name)}/${encodeURIComponent(row.version)}/tree`;
                    const key = rowKey(row);
                    const isIngesting = ingestingKey === key;
                    const feedback =
                      ingestFeedback && ingestFeedback.key === key ? ingestFeedback : null;
                    return (
                      <tr
                        key={`${row.registry}:${row.name}:${row.version}`}
                        className="group cursor-pointer transition-colors hover:bg-bg"
                        onClick={() => navigateTo(treePath)}
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <StatusDot status={row.status === 'indexing' ? 'indexing' : 'done'} />
                            <a
                              href={treePath}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigateTo(treePath);
                              }}
                              className="font-medium text-ink transition-colors group-hover:text-accent hover:underline"
                            >
                              {displayPackageName(row.registry, row.name)}
                            </a>
                            <RegistryBadge registry={row.registry} />
                            {row.sourceUrl && (
                              <a
                                href={row.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-dim hover:text-accent transition-colors"
                                title="Open source repository"
                                aria-label={`Open source repository for ${row.name}`}
                              >
                                <svg
                                  aria-hidden="true"
                                  className="h-3.5 w-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                  />
                                </svg>
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-dim">{row.version}</td>
                        <td className="px-4 py-2.5">
                          {row.id ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAliasTarget(row);
                              }}
                              className={
                                row.alias
                                  ? 'inline-flex items-center gap-1 rounded bg-accent-soft px-2 py-0.5 font-mono text-xs text-accent hover:bg-accent-soft'
                                  : 'text-xs text-dim hover:text-accent'
                              }
                              title={
                                row.alias
                                  ? `Click to edit. Query with @${row.alias} or @${row.alias}@${row.version}`
                                  : 'Add a short alias'
                              }
                            >
                              {row.alias ? `@${row.alias}` : '+ add alias'}
                            </button>
                          ) : (
                            <span className="text-xs text-dim">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-xs text-dim">
                          {isIngesting ? (
                            <span className="text-accent">Re-ingesting…</span>
                          ) : feedback ? (
                            <span
                              className={feedback.tone === 'success' ? 'text-good' : 'text-del'}
                            >
                              {feedback.message}
                            </span>
                          ) : (
                            formatIndexedAt(row.indexedAt)
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <RowActions
                            row={row}
                            onRename={canManage ? () => setAliasTarget(row) : null}
                            onReindex={row.sourceUrl ? () => setReindexUrl(row.sourceUrl!) : null}
                            onReingestVectors={
                              isAdmin ? () => void handleReingestVectors(row) : null
                            }
                            onArchive={canManage ? () => setArchiveTarget(row) : null}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rows.length > PAGE_SIZE && (
                <div className="flex items-center justify-between border-t border-rule-soft px-4 py-2.5 text-xs text-dim">
                  <span>
                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, rows.length)}{' '}
                    of {rows.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      ← Prev
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setPage((p) => ((p + 1) * PAGE_SIZE < rows.length ? p + 1 : p))
                      }
                      disabled={(page + 1) * PAGE_SIZE >= rows.length}
                    >
                      Next →
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Workspaces are SaaS-only — self-host operators rely on their
          project's manifest as the source of truth, not server-side pins. */}
      {deploymentMode === 'saas' && <WorkspacesPanel />}

      <section className="space-y-4">
        <PanelHeader
          title="Repository activity"
          description="Recent private repository indexing jobs for this organization."
          icon={
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.6}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 2 4.5 12.5h6L11 22l8.5-10.5h-6L13 2z"
              />
            </svg>
          }
        />
        <OrgRepoRequestsPanel />
      </section>
    </div>
  );
}

function RowActions({
  row,
  onRename,
  onReindex,
  onReingestVectors,
  onArchive,
}: {
  row: PackageRow;
  /** Null for members: renaming and archiving manage the org (see canManageOrg). */
  onRename: (() => void) | null;
  onReindex: (() => void) | null;
  onReingestVectors: (() => void) | null;
  onArchive: (() => void) | null;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const positionMenu = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const menuWidth = 176;
    const menuHeight = 220;
    const openUp = rect.bottom + menuHeight + 8 > window.innerHeight;
    setMenuPos({
      top: openUp ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4,
      left: Math.max(8, rect.right - menuWidth),
    });
  };

  useEffect(() => {
    if (!open) return;
    positionMenu();
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReposition = () => positionMenu();
    document.addEventListener('mousedown', onClickOutside);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open]);

  if (!row.id) return null;

  return (
    <div className="inline-block">
      <button
        ref={btnRef}
        type="button"
        aria-label="Row actions"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex h-7 w-7 items-center justify-center text-dim hover:bg-bg hover:text-link transition-colors"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="5" cy="12" r="1.75" />
          <circle cx="12" cy="12" r="1.75" />
          <circle cx="19" cy="12" r="1.75" />
        </svg>
      </button>
      {open && menuPos ? (
        <div
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left }}
          className="z-50 w-44 overflow-hidden border border-rule bg-panel py-1 text-left shadow-lg"
        >
          {onRename && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onRename();
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-link hover:bg-bg"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z"
                />
              </svg>
              {row.alias ? 'Edit alias' : 'Add alias'}
            </button>
          )}
          {onReindex ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onReindex();
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-link hover:bg-bg"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                />
              </svg>
              Re-index
            </button>
          ) : null}
          {onReingestVectors ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onReingestVectors();
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-link hover:bg-bg"
              title="Re-embed existing index without re-fetching the repo"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.847-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.091L15.75 12l-2.847.813a4.5 4.5 0 00-3.09 3.091zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"
                />
              </svg>
              Re-ingest vectors
            </button>
          ) : null}
          {row.sourceUrl ? (
            <a
              href={row.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-link hover:bg-bg"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                />
              </svg>
              Open source
            </a>
          ) : null}
          {onArchive && (
            <>
              <div className="my-1 border-t border-rule-soft" />
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onArchive();
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-del hover:bg-del/10"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25v4.5M14 11.25v4.5M3 7.5h18M16.5 7.5v-1.875A1.125 1.125 0 0015.375 4.5h-6.75A1.125 1.125 0 007.5 5.625V7.5"
                  />
                </svg>
                Archive
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function AliasDialog({
  orgId,
  row,
  onClose,
  onSaved,
}: {
  orgId: string;
  row: PackageRow;
  onClose: () => void;
  onSaved: (alias: string | null) => void;
}) {
  const { getToken } = useAuth();
  const [value, setValue] = useState(row.alias ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSaving, onClose]);

  const submit = async (nextAlias: string | null) => {
    if (!row.id) return;
    setIsSaving(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('You must be signed in.');
      const result = await setOrgPackageAlias(orgId, row.id, nextAlias, token);
      onSaved(result.alias);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save alias.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={!isSaving ? onClose : undefined}
      />
      <div className="relative w-full max-w-sm overflow-hidden border border-rule bg-panel shadow-2xl">
        <div className="px-6 py-5 space-y-3">
          <div>
            <h3 className="text-base font-semibold text-ink">Repo alias</h3>
            <p className="mt-1 text-sm text-dim">
              A short name to reference <span className="font-mono text-link">{row.name}</span>.
              Applies to every indexed version.
            </p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit(value.trim() || null);
            }}
            className="space-y-2"
          >
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-dim">@</span>
              <input
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="e.g. ui-kit"
                className="lc-input flex-1"
                pattern="[a-z0-9][a-z0-9\-_]{0,38}[a-z0-9]?"
                maxLength={40}
                disabled={isSaving}
              />
            </div>
            <p className="text-xs text-dim">
              Lowercase letters, numbers, hyphens or underscores. Query with{' '}
              <span className="font-mono">@{value || 'alias'}</span> or{' '}
              <span className="font-mono">
                @{value || 'alias'}@{row.version}
              </span>
              .
            </p>
            {error ? <p className="text-xs text-del">{error}</p> : null}
          </form>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-rule-soft px-6 py-3">
          {row.alias ? (
            <button
              type="button"
              onClick={() => void submit(null)}
              disabled={isSaving}
              className="text-xs text-del hover:underline disabled:opacity-50"
            >
              Remove alias
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-3 py-1.5 text-sm font-medium text-link hover:bg-bg disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="button"
              size="sm"
              onClick={() => void submit(value.trim() || null)}
              disabled={isSaving || value.trim() === (row.alias ?? '')}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
